// frontend/src/pages/Company/ViewCompany.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { Building2, Calendar, MapPin, ArrowLeft, Edit, Users, UserPlus, Tag, DollarSign, Globe, Mail, Phone, UserCheck, Image as ImageIcon } from "lucide-react";
import "./ViewCompany.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ViewCompany = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);

  useEffect(() => {
    fetchCompanyDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);

      // Fetch company details
      const companyRes = await axios.get(`${url}/api/company/${id}`, {
        headers: getAuthHeaders(),
      });
      const companyData = companyRes.data.data;
      setCompany(companyData);

      // Get users from the company data
      if (companyData.users) {
        setCompanyUsers(companyData.users);
      } else {
        setCompanyUsers([]);
      }

    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch company details");
      navigate('/ViewCompany');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/company/edit/${id}`);
  };

  const handleAddUser = () => {
    navigate(`/company/${id}/add-user`);
  };

  const handleBack = () => {
    navigate('/ViewCompany');
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "Not set";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const getCurrencyDisplay = (currency) => {
    if (!currency) return "($)";
    return `${currency.symbol || ''} (${currency.name || ''})`;
  };

  // Helper to get full logo URL
  const getLogoUrl = () => {
    if (company && company.logo) {
      // Remove leading slash if any, and ensure proper concatenation
      const logoPath = company.logo.startsWith('/') ? company.logo.slice(1) : company.logo;
      return `${url}/${logoPath}`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="company-form-container">
          <div className="loading-container">
            <div className="animate-spin" style={{ width: '32px', height: '32px', border: '3px solid #f89a3a', borderTopColor: 'transparent', borderRadius: '50%' }}></div>
            <p>Loading company details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="form-wrapper">
        <div className="company-form-container">
          <div className="no-data-content">
            <Building2 size={48} />
            <p>Company not found</p>
            <button className="clear-search" onClick={handleBack}>
              Back to Companies
            </button>
          </div>
        </div>
      </div>
    );
  }

  const logoUrl = getLogoUrl();

  return (
    <div className="form-wrapper">
      <div className="company-form-container">
        {/* Back button */}
        <button className="back-btn" onClick={handleBack} style={{ marginBottom: '20px' }}>
          <ArrowLeft size={16} className="mr-2" />
          Back to Companies
        </button>

        {/* Header with Logo */}
        <div className="view-company-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
            {/* Logo Display */}
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`${company.company_name} logo`}
                style={{ width: '80px', height: '80px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #ddd', background: '#fff' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling?.style?.setProperty('display', 'flex');
                }}
              />
            ) : (
              <div style={{
                width: '80px', height: '80px',
                background: '#f5f5f5',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px dashed #ccc'
              }}>
                <ImageIcon size={32} color="#aaa" />
              </div>
            )}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px', flexWrap: 'wrap' }}>
                <h1 className="form-title" style={{ margin: 0 }}>{company.company_name}</h1>
                <span className="company-code" style={{ fontSize: '16px' }}>
                  {company.company_code}
                </span>
              </div>
              <p className="form-subtitle" style={{ marginBottom: 0 }}>
                Company details
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button className="add-user-btn" onClick={handleAddUser}>
              <UserPlus size={16} className="mr-2" />
              Add User
            </button>
            <button className="submit-btn" onClick={handleEdit}>
              <Edit size={16} className="mr-2" />
              Edit Company
            </button>
          </div>
        </div>

        {/* Company Details Grid (3 cards) */}
        <div className="company-details-grid">
          <div className="detail-card">
            <h3><Building2 size={18} className="mr-2" /> Company Information</h3>
            <div className="detail-item">
              <span className="detail-label">Company Name</span>
              <span className="detail-value">{company.company_name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Company Code</span>
              <span className="detail-value">{company.company_code}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Company ID</span>
              <span className="detail-value">{company.company_id}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className={`status-badge ${company.status}`} style={{ display: 'inline-block' }}>
                {company.status || "active"}
              </span>
            </div>
          </div>

          <div className="detail-card">
            <h3><Tag size={18} className="mr-2" /> Business Information</h3>
            <div className="detail-item">
              <span className="detail-label">Business Type</span>
              <span className="detail-value">{company.business_type?.name || "Not specified"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Fiscal Year Start</span>
              <span className="detail-value">{formatDate(company.fiscal_year_start)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Base Currency</span>
              <span className="detail-value">
                {getCurrencyDisplay(company.base_currency)}
              </span>
            </div>
          </div>

          <div className="detail-card">
            <h3><Globe size={18} className="mr-2" /> Location & Metadata</h3>
            <div className="detail-item">
              <span className="detail-label">Country</span>
              <span className="detail-value">{company.country?.name || "Not specified"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Created</span>
              <span className="detail-value">{formatDateTime(company.createdAt)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Last Updated</span>
              <span className="detail-value">{formatDateTime(company.updatedAt)}</span>
            </div>
          </div>
        </div>

        {/* Optional: separate logo card if you prefer, but we already placed in header */}

        {/* Created By Information */}
        {/* Created By Information */}
{company.created_by && (
  <div style={{
    marginTop: '20px',
    padding: '20px',
    background: 'rgba(18, 19, 73, 0.05)',
    borderRadius: '10px',
    borderLeft: '4px solid #f89a3a'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
      <UserCheck size={20} color="#121349" />
      <h4 style={{ color: '#121349', margin: 0 }}>Company Admin (Created By)</h4>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
      {/* Admin Name */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
          <Users size={16} />
          <small style={{ color: '#666', fontWeight: '600' }}>Admin Name</small>
        </div>
        <p style={{ margin: '5px 0 10px 0', fontWeight: '600', fontSize: '16px' }}>
          {company.created_by.Usercomp_sure_name}
        </p>
      </div>

      {/* Email */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
          <Mail size={16} />
          <small style={{ color: '#666', fontWeight: '600' }}>Email</small>
        </div>
        <p style={{ margin: '5px 0 10px 0' }}>{company.created_by.Usercomp_email}</p>
      </div>

      {/* ✅ NEW: Phone(s) — place it HERE */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
          <Phone size={16} />
          <small style={{ color: '#666', fontWeight: '600' }}>Phone(s)</small>
        </div>
        <p style={{ margin: '5px 0 10px 0' }}>
          {company.created_by.Usercomp_contact || "—"}
          {company.created_by.Usercomp_contact_2 ? ` · ${company.created_by.Usercomp_contact_2}` : ""}
          {company.created_by.Usercomp_contact_3 ? ` · ${company.created_by.Usercomp_contact_3}` : ""}
        </p>
      </div>

      {/* User Code */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
          <Tag size={16} />
          <small style={{ color: '#666', fontWeight: '600' }}>User Code</small>
        </div>
        <p style={{ margin: '5px 0 10px 0' }}>
          <span className="company-code" style={{ fontSize: '12px' }}>
            {company.created_by.Usercomp_code}
          </span>
        </p>
      </div>
    </div>
  </div>
)}

        {/* Company Users Section */}
        <div className="company-users">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', marginTop: '30px' }}>
            <h3 style={{ color: '#121349', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Users size={20} />
              Company Users ({companyUsers.length})
            </h3>
            <button className="add-user-btn" onClick={handleAddUser}>
              <UserPlus size={16} className="mr-2" />
              Add New User
            </button>
          </div>

          {companyUsers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', background: '#f8f9fa', borderRadius: '10px', border: '1px dashed #ddd' }}>
              <Users size={48} color="#ccc" />
              <p style={{ margin: '15px 0', color: '#666', fontSize: '16px' }}>No users found for this company</p>
              <button className="add-user-btn" onClick={handleAddUser}>
                <UserPlus size={16} className="mr-2" />
                Add First User
              </button>
            </div>
          ) : (
            <div className="table-container">
              <table className="styled-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User Code</th>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Contact</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {companyUsers.map((user, index) => (
                    <tr key={user._id}>
                      <td>{index + 1}</td>
                      <td><span className="company-code" style={{ fontSize: '12px' }}>{user.Usercomp_code || "N/A"}</span></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {user.role === 'admin' && <UserCheck size={14} color="#f89a3a" />}
                          <strong>{user.Usercomp_sure_name || "N/A"}</strong>
                        </div>
                      </td>
                      <td>{user.Usercomp_email || "N/A"}</td>
                      <td>{user.Usercomp_contact || "N/A"}</td>
                      <td>{user.Usercomp_contact2 || "N/A"}</td>
                      <td>{user.Usercomp_contact3 || "N/A"}</td>
                      <td>
                        <span className={`type-badge ${user.role}`} style={{
                          background: user.role === 'admin' ? 'rgba(248, 154, 58, 0.1)' :
                                    user.role === 'sales' ? 'rgba(46, 125, 50, 0.1)' :
                                    user.role === 'cashier' ? 'rgba(33, 150, 243, 0.1)' :
                                    user.role === 'purchase' ? 'rgba(156, 39, 176, 0.1)' :
                                    'rgba(158, 158, 158, 0.1)',
                          color: user.role === 'admin' ? '#f89a3a' :
                                 user.role === 'sales' ? '#2e7d32' :
                                 user.role === 'cashier' ? '#2196f3' :
                                 user.role === 'purchase' ? '#9c27b0' :
                                 '#666',
                          borderColor: user.role === 'admin' ? 'rgba(248, 154, 58, 0.3)' :
                                       user.role === 'sales' ? 'rgba(46, 125, 50, 0.3)' :
                                       user.role === 'cashier' ? 'rgba(33, 150, 243, 0.3)' :
                                       user.role === 'purchase' ? 'rgba(156, 39, 176, 0.3)' :
                                       'rgba(158, 158, 158, 0.3)'
                        }}>
                          {user.role || "user"}
                        </span>
                      </td>
                      <td>
                        <span className={`user-status-badge ${user.Usercomp_status}`}>
                          {user.Usercomp_status}
                        </span>
                      </td>
                      <td>{formatDate(user.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="view-actions">
          <button className="cancel-btn" onClick={handleBack}>
            Back to List
          </button>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="add-user-btn" onClick={handleAddUser}>
              <UserPlus size={16} className="mr-2" />
              Add User
            </button>
            <button className="submit-btn" onClick={handleEdit}>
              <Edit size={16} className="mr-2" />
              Edit Company
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewCompany;
